import { memo, useMemo, useState } from 'react'
import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    Select,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'
import {
    getColorSelectorActiveOutlineSx,
    getColorSelectorMatchedFieldOutlineSx,
} from '../../../colorSelector/colorSelectorChrome'

const SelectDropdownIcon = ({ sx, ...props }: SvgIconProps) => (
    <SvgIcon
        {...props}
        viewBox="0 0 24 24"
        sx={{ fontSize: 18, width: 18, height: 18, ...sx }}
    >
        <path d="M6.5 9L12 14.5L17.5 9" stroke="currentColor" fill="none" />
    </SvgIcon>
)

const SECTION_HEADER = 'OUTPUT VALUES'
const PRECISION_LABEL = 'Precision'
const LABELS_ON_PRICE_SCALE = 'Labels on price scale'
const VALUES_ON_PRICE_SCALE = 'Values on price scale'

export const OUTPUT_VALUES_PRECISION_OPTIONS = [
    'Default',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
] as const

export type OutputValuesPrecisionOption = (typeof OUTPUT_VALUES_PRECISION_OPTIONS)[number]

export const outputValuesPrecisionToOption = (precision?: number): OutputValuesPrecisionOption =>
    precision !== undefined && precision >= 0 && precision <= 8
        ? (String(precision) as OutputValuesPrecisionOption)
        : 'Default'

export const outputValuesPrecisionFromOption = (option: OutputValuesPrecisionOption): number | undefined =>
    option === 'Default' ? undefined : Number(option)

const settingsRowSx = { py: '8px', px: 0 }

const styleLabelColumnSx = { p: 0, minWidth: '90px' }

const outputControlsSx = { p: 0, display: 'flex', alignItems: 'center', gap: 1 }

const sectionHeaderRowSx = {
    pt: '8px',
    pb: 0,
    px: 0,
    boxSizing: 'border-box' as const,
    alignItems: 'center',
}

const checkboxRowSx = {
    py: '4px',
    px: 0,
    minHeight: 36,
    boxSizing: 'border-box' as const,
    alignItems: 'center',
}

const plotSettingsCheckboxSx = { p: 0, mr: 0.5 } as const

const plotSettingsCheckboxLabelSx = {
    ml: 0,
    mr: 0,
    alignItems: 'center',
    cursor: 'pointer',
} as const

const inputLabelProps = {
    variant: 'body2' as const,
    color: 'text.secondary' as const,
    fontWeight: 400,
}

const SELECT_ICON_RESERVE_PX = 20

export type SeriesGroupOutputValuesSettingsProps = {
    outputValuesPrecision?: number
    showLabelOnPriceScale?: boolean
    showValueOnPriceScale?: boolean
    onOutputValuesPrecisionChange: (precision: number | undefined) => void
    onShowLabelOnPriceScaleChange: (value: boolean) => void
    onShowValueOnPriceScaleChange: (value: boolean) => void
}

const SeriesGroupOutputValuesSettings = ({
    outputValuesPrecision,
    showLabelOnPriceScale,
    showValueOnPriceScale,
    onOutputValuesPrecisionChange,
    onShowLabelOnPriceScaleChange,
    onShowValueOnPriceScaleChange,
}: SeriesGroupOutputValuesSettingsProps) => {
    const theme = useTheme()
    const [open, setOpen] = useState(false)
    const selectedOption = outputValuesPrecisionToOption(outputValuesPrecision)

    const selectControlSx = useMemo(
        (): SystemStyleObject<Theme> => ({
            width: '100px',
            flexShrink: 0,
            ...getColorSelectorMatchedFieldOutlineSx(theme),
            '& .MuiInputBase-root': {
                height: '34px',
                minHeight: '34px',
            },
            '& .MuiSelect-select': {
                padding: `6px ${SELECT_ICON_RESERVE_PX}px 6px 10px !important`,
                minHeight: '22px !important',
                boxSizing: 'border-box' as const,
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            '& .MuiOutlinedInput-input': {
                paddingRight: `${SELECT_ICON_RESERVE_PX}px !important`,
            },
            '& .MuiSelect-icon': {
                fontSize: '18px',
                width: '18px',
                height: '18px',
                top: 'calc(50% - 9px)',
                right: '2px',
                '& svg': {
                    width: '18px',
                    height: '18px',
                },
            },
        }),
        [theme]
    )

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={sectionHeaderRowSx} display="flex" alignItems="center">
                <Typography fontSize="12px" color="#6a6d78">
                    {SECTION_HEADER}
                </Typography>
            </Box>
            <Grid container sx={settingsRowSx} alignItems="center">
                <Grid item xs={6} sx={styleLabelColumnSx}>
                    <Typography {...inputLabelProps} noWrap title={PRECISION_LABEL}>
                        {PRECISION_LABEL}
                    </Typography>
                </Grid>
                <Grid item xs={6} sx={outputControlsSx}>
                    <Select
                        value={selectedOption}
                        size="small"
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        sx={{
                            ...selectControlSx,
                            ...(open ? getColorSelectorActiveOutlineSx(theme) : undefined),
                        }}
                        IconComponent={SelectDropdownIcon}
                        renderValue={(value) => (
                            <Typography
                                component="span"
                                variant="body2"
                                noWrap
                                title={String(value ?? '')}
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: '22px',
                                }}
                            >
                                {value}
                            </Typography>
                        )}
                        onChange={({ target }) =>
                            onOutputValuesPrecisionChange(
                                outputValuesPrecisionFromOption(target.value as OutputValuesPrecisionOption)
                            )
                        }
                    >
                        {OUTPUT_VALUES_PRECISION_OPTIONS.map((option) => (
                            <MenuItem value={option} key={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            </Grid>
            <Box sx={{ ...checkboxRowSx, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            disableRipple
                            size="small"
                            sx={plotSettingsCheckboxSx}
                            checked={showLabelOnPriceScale ?? true}
                            onChange={(_event, checked) => onShowLabelOnPriceScaleChange(checked)}
                        />
                    }
                    label={
                        <Typography {...inputLabelProps} noWrap title={LABELS_ON_PRICE_SCALE}>
                            {LABELS_ON_PRICE_SCALE}
                        </Typography>
                    }
                    sx={plotSettingsCheckboxLabelSx}
                />
            </Box>
            <Box sx={{ ...checkboxRowSx, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            disableRipple
                            size="small"
                            sx={plotSettingsCheckboxSx}
                            checked={showValueOnPriceScale ?? true}
                            onChange={(_event, checked) => onShowValueOnPriceScaleChange(checked)}
                        />
                    }
                    label={
                        <Typography {...inputLabelProps} noWrap title={VALUES_ON_PRICE_SCALE}>
                            {VALUES_ON_PRICE_SCALE}
                        </Typography>
                    }
                    sx={plotSettingsCheckboxLabelSx}
                />
            </Box>
        </Box>
    )
}

export default memo(SeriesGroupOutputValuesSettings)
